import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { ArrowLeft, Calendar, User, Users, Share2, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog?.title,
        text: blog?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h2>
          <p className="text-slate-600 mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/blog')} className="bg-slate-900 hover:bg-slate-800">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-8 w-8 object-contain" />
              <span className="text-2xl font-semibold text-slate-900">JurisGo</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Home</Link>
              <Link to="/trainings" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Trainings</Link>
              <Link to="/opportunities" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Opportunities</Link>
              <Link to="/blog" className="text-slate-900 font-semibold border-b-2 border-slate-900">Blog</Link>
              <Link to="/corporate" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Corporate</Link>
            </nav>
            <div className="flex items-center space-x-3">
              <Button asChild variant="outline" className="border-green-500 text-green-600 hover:bg-green-50 transition-colors">
                <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span className="hidden lg:inline">Community</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/blog')}
          className="text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to All Articles
        </Button>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Header Image */}
        {blog.image && (
          <div className="aspect-video rounded-2xl overflow-hidden mb-8 shadow-lg">
            <img 
              src={blog.image} 
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {blog.category && (
            <Badge className="bg-slate-900 text-white">
              {blog.category}
            </Badge>
          )}
          <div className="flex items-center text-sm text-slate-500">
            <Calendar className="h-4 w-4 mr-1" />
            {new Date(blog.created_at || blog.date).toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </div>
          <div className="flex items-center text-sm text-slate-500">
            <User className="h-4 w-4 mr-1" />
            {blog.author}
          </div>
          <div className="flex items-center text-sm text-slate-500">
            <Clock className="h-4 w-4 mr-1" />
            {Math.ceil((blog.content?.length || 0) / 1000)} min read
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
          {blog.title}
        </h1>

        {/* Excerpt */}
        {blog.excerpt && (
          <p className="text-xl text-slate-600 mb-8 leading-relaxed border-l-4 border-slate-900 pl-4">
            {blog.excerpt}
          </p>
        )}

        {/* Content */}
        <div className="prose prose-lg prose-slate max-w-none">
          {blog.content && blog.content.includes('<') ? (
            // Render HTML content
            <div 
              className="blog-html-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          ) : (
            // Render plain text content
            <div 
              className="text-slate-700 leading-relaxed whitespace-pre-wrap"
              style={{ fontSize: '1.125rem', lineHeight: '1.8' }}
            >
              {blog.content}
            </div>
          )}
        </div>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 text-sm">Found this article helpful?</p>
              <p className="text-slate-900 font-medium">Share it with your network</p>
            </div>
            <Button onClick={handleShare} variant="outline" className="border-slate-300">
              <Share2 className="mr-2 h-4 w-4" /> Share Article
            </Button>
          </div>
        </div>

        {/* Author Card */}
        <div className="mt-8 p-6 bg-slate-100 rounded-2xl">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-900 flex items-center justify-center text-white text-xl font-bold">
              {blog.author?.charAt(0) || 'J'}
            </div>
            <div>
              <p className="text-sm text-slate-500">Written by</p>
              <p className="text-lg font-semibold text-slate-900">{blog.author}</p>
              <p className="text-slate-600 mt-1">JurisGo Team</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-slate-900 rounded-2xl text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Ready to Start Your Paralegal Journey?</h3>
          <p className="text-slate-300 mb-6">Join our training programs and become a certified international paralegal</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-white text-slate-900 hover:bg-slate-100">
              <Link to="/trainings">View Programs</Link>
            </Button>
            <Button asChild variant="outline" className="border-slate-400 text-white hover:bg-slate-800">
              <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer">
                <Users className="mr-2 h-4 w-4" /> Join Community
              </a>
            </Button>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-6 w-6 object-contain" />
                <span className="text-xl font-semibold text-white">JurisGo</span>
              </div>
              <p className="text-slate-400">India's Premier International Paralegal Training Platform</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
              <p className="mb-2">Email: {mockData.footer.email}</p>
              <p className="mb-2">Phone: {mockData.footer.phone}</p>
              <p>Location: {mockData.footer.address}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/trainings" className="hover:text-white transition-colors">Trainings</Link></li>
                <li><Link to="/opportunities" className="hover:text-white transition-colors">Opportunities</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/corporate" className="hover:text-white transition-colors">Corporate</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8">
            <p className="text-sm text-slate-400 mb-4">{mockData.footer.disclaimer}</p>
            <p className="text-center text-slate-500">{mockData.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogDetail;
